import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fmtMoney, fmtMoney2, fmtPct, GRADE_BADGE, STATUS_BADGE, CATEGORY_LABELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import OfferDialog from "@/components/app/OfferDialog";
import FeatureListingButton from "@/components/app/FeatureListingButton";
import YieldCalculator from "@/components/app/YieldCalculator";
import { ArrowLeft, FileText } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import DocumentUpload from "@/components/app/DocumentUpload";
import { useSEO, jsonLdBreadcrumb } from "@/lib/seo";
import { trackNoteViewed } from "@/lib/analytics";

export default function NoteDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [note, setNote] = useState(null);
  const [active, setActive] = useState(0);
  const [offer, setOffer] = useState(false);

  useEffect(() => {
    api.get(`/notes/${id}`).then((r) => {
      setNote(r.data);
      trackNoteViewed(r.data);
    }).catch(() => navigate("/notes"));
  }, [id, navigate]);

  const vehicleLabel = note ? `${note.vehicle_year} ${note.vehicle_make} ${note.vehicle_model}` : "";
  const noteDetailUrl = `https://myfinancelocal.com/notes/${id}`;

  useSEO(note ? {
    title: `${vehicleLabel} — ${fmtPct(note.estimated_yield)} Yield Vehicle Note | FinanceLocal`,
    description: `${vehicleLabel} vehicle note in ${note.state}. ${fmtPct(note.estimated_yield)} estimated yield · ${fmtMoney(note.remaining_balance)} balance · ${fmtMoney(note.asking_price)} asking price. Grade ${note.grade}, ${CATEGORY_LABELS[note.category] || note.category}.`,
    canonical: noteDetailUrl,
    keywords: `${vehicleLabel} note, ${note.vehicle_make} auto note, cash-flow note, ${note.state} vehicle note, grade ${note.grade} note, vehicle-backed note investing`,
    jsonLd: [
      jsonLdBreadcrumb([
        { name: "Home", url: "https://myfinancelocal.com/" },
        { name: "Available Notes", url: "https://myfinancelocal.com/notes" },
        { name: vehicleLabel, url: noteDetailUrl },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "FinancialProduct",
        "name": `${vehicleLabel} Vehicle-Backed Note`,
        "url": noteDetailUrl,
        "category": "Vehicle-backed promissory note",
        "provider": { "@type": "Organization", "name": "FinanceLocal", "url": "https://myfinancelocal.com/" },
        "feesAndCommissionsSpecification": "Investor pays asking price plus any Stripe processing fee. No FinanceLocal commission on the note price.",
        "interestRate": note.interest_rate,
        "annualPercentageRate": note.estimated_yield,
        "amount": { "@type": "MonetaryAmount", "currency": "USD", "value": note.asking_price },
        "description": (note.description || "").slice(0, 400),
      },
      {
        "@context": "https://schema.org",
        "@type": "Offer",
        "url": noteDetailUrl,
        "priceCurrency": "USD",
        "price": note.asking_price,
        "availability": note.listed ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
        "itemOffered": {
          "@type": "Product",
          "name": `${vehicleLabel} Vehicle Note (Grade ${note.grade})`,
          "category": CATEGORY_LABELS[note.category] || note.category,
        },
        "seller": { "@type": "Organization", "name": "FinanceLocal Marketplace" },
      },
    ],
  } : { title: "Vehicle Note — FinanceLocal", canonical: noteDetailUrl });

  if (!note) {
    return <div className="mx-auto max-w-7xl px-4 py-16 text-muted-foreground" data-testid="note-loading">Loading…</div>;
  }

  const photos = (note.photos && note.photos.length) ? note.photos : [note.vehicle_photo];

  return (
    <div data-testid="page-note-details" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/notes" data-testid="back-to-notes" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Notes
      </Link>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10">
        <div>
          <div className="border border-border aspect-[16/10] overflow-hidden bg-muted">
            <img src={photos[active]} alt={`${vehicleLabel} — photo ${active + 1}`} className="w-full h-full object-cover" />
          </div>
          {photos.length > 1 && (
            <div className="grid grid-cols-4 gap-2 mt-3">
              {photos.map((p, i) => (
                <button key={i} onClick={() => setActive(i)}
                  className={`border aspect-[4/3] overflow-hidden ${i === active ? "border-primary" : "border-border"}`}>
                  <img src={p} alt={`${vehicleLabel} thumbnail ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-10 border-t border-border pt-6">
            <div className="font-overline mb-3">Description</div>
            <p className="text-muted-foreground leading-relaxed">{note.description}</p>
          </div>

          <div className="mt-10">
            <YieldCalculator note={note} />
          </div>

          <div className="mt-10 border-t border-border pt-6">
            <div className="font-overline mb-4">Supporting Documents</div>
            {user ? (
              <DocumentUpload
                entityType="note"
                entityId={note.note_id}
                isAdmin={user.role === "admin"}
                readOnly={user.role !== "admin"}
                showAll
                title=""
              />
            ) : (
              <div className="text-sm text-muted-foreground">
                <a href="/login" className="underline">Sign in</a> to view supporting documents for this note.
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <div className="font-overline text-muted-foreground">{note.vehicle_year} · {note.state}</div>
            <h1 data-testid="note-title" className="font-display text-4xl mt-2 leading-tight">
              {note.vehicle_make} {note.vehicle_model}
            </h1>
            <div className="mt-3 flex gap-2">
              <span className={`px-2 py-1 text-[10px] font-mono-data uppercase tracking-wider ${GRADE_BADGE[note.grade]}`}>Grade {note.grade}</span>
              <span className={`px-2 py-1 text-[10px] font-mono-data uppercase tracking-wider ${STATUS_BADGE[note.category]}`}>{CATEGORY_LABELS[note.category]}</span>
            </div>
          </div>

          <div className="border border-border p-6 space-y-1">
            <div className="font-overline text-muted-foreground">Estimated Yield</div>
            <div className="font-mono-data text-5xl text-primary font-light tracking-tighter" data-testid="note-yield">{fmtPct(note.estimated_yield)}</div>
          </div>

          <div className="border border-border divide-y divide-border">
            <Row label="Asking Price" value={fmtMoney(note.asking_price)} highlight testId="note-asking" />
            <Row label="Original Loan" value={fmtMoney(note.original_loan_amount)} />
            <Row label="Remaining Balance" value={fmtMoney(note.remaining_balance)} testId="note-balance" />
            <Row label={
              note.payment_frequency === "monthly" ? "Monthly Payment" :
              note.payment_frequency === "biweekly" ? "Bi-weekly Payment" : "Weekly Payment"
            } value={fmtMoney2(note.weekly_payment)} />
            <Row label="Interest Rate" value={fmtPct(note.interest_rate)} />
            <Row label="Remaining Term" value={`${note.remaining_term_weeks} ${
              note.payment_frequency === "monthly" ? "months" :
              note.payment_frequency === "biweekly" ? "bi-weeks" : "weeks"
            }`} />
            <Row label="Payments Made" value={note.payments_made} />
            <Row label="Late Payments" value={note.late_payments} />
            <Row label="Vehicle Mileage" value={`${(note.vehicle_mileage || 0).toLocaleString()} mi`} />
          </div>

          <Button data-testid="details-make-offer" disabled={!note.listed} onClick={() => setOffer(true)}
            className="w-full rounded-none uppercase tracking-wider h-12">
            {note.listed ? "Submit Offer" : "Note Off Market"}
          </Button>

          {user && (user.role === "admin" || user.user_id === note.seller_id) && note.listed && !note.featured_until && (
            <div className="border border-border p-4" data-testid="feature-section">
              <div className="font-overline text-[10px] text-muted-foreground mb-2">Boost this Listing</div>
              <p className="text-xs text-muted-foreground mb-3">
                Feature your listing at the top of the marketplace — pay via secure Stripe Checkout.
              </p>
              <FeatureListingButton noteId={note.note_id} className="w-full" size="lg" />
            </div>
          )}
        </div>
      </div>

      <OfferDialog note={note} open={offer} onOpenChange={setOffer} />
    </div>
  );
}

function Row({ label, value, highlight, testId }) {
  return (
    <div className="flex items-center justify-between px-5 py-3" data-testid={testId}>
      <div className="font-overline text-muted-foreground">{label}</div>
      <div className={`font-mono-data ${highlight ? "text-primary text-lg" : ""}`}>{value}</div>
    </div>
  );
}
